// node_modules
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChartData, ElementChartOptions, PluginChartOptions, ScaleChartOptions } from "chart.js";
import { _DeepPartialObject } from "chart.js/types/utils";
import { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
// import ContentLoader from "react-content-loader";
// Contexts
import { SavedQueryContext, WebsocketContext } from "Providers";
import { CaseContext } from "Stores";
// Components
import { AsynchronousWrapper, BarChart, FindestButton, Pagination, PartnerResult, RotatingCircleLoadingIndicator } from "Components";
import { FindOrganizationsQueryFields } from "../FindOrganizationsQueryFields/FindOrganizationsQueryFields";
import { SavedQueries } from "../SavedQueries/SavedQueries";
import { SavedQueryFields } from "../SavedQueryFields/SavedQueryFields";
// Types
import { TConcept, TFindOrganizationsDTO, TPartnerResultDTO, TTechnology, TUseFetch } from "Types";
// Custom hooks
import { useFetch, usePagination } from "Hooks";
// Enums
import { CaseActionTypeEnum, DocumentTypeEnum, PartnerTimeRangeEnum, ToastTypeEnum } from "Enums";
// styles
import styles from "./findOrganizations.module.scss";
// Constants
import { LoggingConstants, RegexConstants, TechnologyConstants } from "Constants";
// Interfaces
import { ICaseSelectedTechnology, ICaseSetQueryCount } from "Interfaces";
// Logics
import { BookmarkLogicSingleton } from "Logics";
import { QueryTabs } from "../QueryTabs";
// Helpers
import { PendoHelperSingleton, ToastHelperSingleton } from "Helpers";

// const SearchButtonLoader = () => (
//     <ContentLoader
//         animate={true}
//         height="24"
//         width="90"
//         speed={2}
//         backgroundColor="#fcfcfc"
//         foregroundColor="#ecebeb"
//     >
//         <rect x="0" y="0" rx="5" ry="5" width="90" height="24" /> 
//     </ContentLoader>
// );
// 
// const ViewQueryLoader = () => (
//     <ContentLoader
//         animate={true}
//         height="15"
//         width="100%"
//         speed={2}
//         backgroundColor="#fcfcfc"
//         foregroundColor="#ecebeb"
//     >
//         <rect x="32" y="0" rx="5" ry="5" width="70%" height="15" /> 
//     </ContentLoader>
// );

export const FindOrganizations: FC = () => {
    // Const
    const amountOfResultsToSearchFor = 20;

    // Ref
    const customStartYearBoxRef = useRef<HTMLInputElement>(null);
    const customEndYearBoxRef = useRef<HTMLInputElement>(null);

    // Search form state
    const [technologies, setTechnologies] = useState<TTechnology[]>([]);
    const [selectedOriginalTechnology, setSelectedOriginalTechnology] = useState<TTechnology | undefined>(undefined);
    const [timeRange, setTimeRange] = useState<PartnerTimeRangeEnum>(PartnerTimeRangeEnum.Last10Years);
    const [doFindUniversities, setDoFindestUniversities] = useState<boolean>(true);
    const [doFindResearchInstitutes, setDoFindResearchInstitutes] = useState<boolean>(true);
    const [doFindCompanies, setDoFindestCompanies] = useState<boolean>(true);
    const [isQueryCollapsed, setIsQueryCollapsed] = useState<boolean>(true);

    // Search results state
    const [mostActiveOrganizationData, setMostActiveOrganizationData] = useState<ChartData<"bar"> | undefined>(undefined);
    const [publicationsOverTimeData, setPublicationsOverTimeData] = useState<ChartData<"bar"> | undefined>(undefined);
    const [partners, setPartners] = useState<TPartnerResultDTO[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [partnerCount, setPartnerCount] = useState<number | undefined>(undefined);
    const [alreadyPaginatedPartners, setAlreadyPaginatedPartners] = useState<string[]>([]);
    const [searchId, setSearchId] = useState<string | undefined>(undefined);

    // Contexts
    const { webSocketController } = useContext(WebsocketContext);
    const { caseState, caseDispatch } = useContext(CaseContext);
    const { savedQuery, isSavedQueriesLoading } = useContext(SavedQueryContext);

    // Query count state
    const [caseQueryCount, setCaseQueryCount] = useState<number>(caseState.selectedCase?.queryCount ? caseState.selectedCase?.queryCount : 0);

    const isAllowedToPaginate = (): boolean => {
        if (isSearching) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Please wait untill the current search is finished.");
            return false;
        }

        return true;
    };

    const onPaginate = (newPageNumber: number) => {
        searchClicked(newPageNumber);
    };

    // Let
    const { isLoading: isConceptListLoading, didError: didConceptListError, errorMessage: conceptListErrorMessage, fetchedData: fetchedConceptList }: TUseFetch<TConcept[]> = useFetch(`api/cases/${caseState.selectedCaseId}/concepts?doIncludeAdditional=true`);
    const {currentPage, amountOfPages, paginateToNextPage, paginateToPreviousPage} = usePagination(amountOfResultsToSearchFor, partnerCount, onPaginate, isAllowedToPaginate);

    // Logic
    useEffect(() => {
        if (!fetchedConceptList || fetchedConceptList.length <= 0) {
            setTechnologies([]);
        } else {
            const newTechnologyList: TTechnology[] = [];
            for (const concept of fetchedConceptList) {
                newTechnologyList.push(...concept.technologies);
            }
            setTechnologies(newTechnologyList);
        }
    }, [fetchedConceptList]);

    useEffect(() => {
        for (const technology of technologies) {
            if ((!technology.isUserAdded && technology.id === caseState.selectedOriginalTechnologyId) || 
                    (technology.isUserAdded && technology.id === caseState.selectedTechnologyId)) {
                setSelectedOriginalTechnology(technology);
                break;
            }
        }
    }, [caseState.selectedOriginalTechnologyId, caseState.selectedTechnologyId, technologies]);

    const receivePartnerCount = useCallback((newPartnerCount: number) => {
        if (currentPage !== 1) return;
        setPartnerCount(newPartnerCount);
    }, [currentPage]);

    const createdStackBarChartOptions = (title?: string): _DeepPartialObject<ElementChartOptions<"bar"> & PluginChartOptions<"bar"> & ScaleChartOptions<"bar">> => {
        return {
            plugins: {
                title: {
                    display: true,
                    text: title
                },
            },
            scales: {
                x: {
                    stacked: true
                },
                y: {
                    stacked: true
                }
            }
        };
    };

    const receivePartnerResults = useCallback((partnerResults: TPartnerResultDTO[]) => {
        const labels: string[] = [];
        const scienceDatasetData: number[] = [];
        const patentDatasetData: number[] = [];

        if (currentPage === 1) {
            partnerResults.forEach(partner => {
                labels.push(partner.name);
                scienceDatasetData.push(partner.totalScienceArticleCount);
                patentDatasetData.push(partner.totalPatentCount);
            });

            setMostActiveOrganizationData({
                labels: labels,
                datasets: [
                    {
                        label: "Science",
                        data: scienceDatasetData,
                        backgroundColor: "rgb(54, 162, 235)"
                    },
                    {
                        label: "Patents",
                        data: patentDatasetData,
                        backgroundColor: "rgb(75, 192, 192)"
                    }
                ]
            });
        }

        setIsSearching(false);
        setPartners(partnerResults);
        let partnerNames = partnerResults.map((partner => {
            return partner.name;
        }));
        partnerNames = partnerNames.concat(alreadyPaginatedPartners);
        setAlreadyPaginatedPartners(partnerNames);
    }, [currentPage, alreadyPaginatedPartners]);

    const receiveSearchId = useCallback((newSearchId: string) => {
        setSearchId(newSearchId);
    }, [setSearchId]);

    const getCustomStartYear = () => {
        let customStartYear: number | undefined = undefined;
        if (customStartYearBoxRef.current) {
            customStartYear = parseInt(customStartYearBoxRef.current.value);
        }

        return customStartYear;
    };

    const getCustomEndYear = () => {
        let customEndYear: number | undefined = undefined;
        if (customEndYearBoxRef.current) {
            customEndYear = parseInt(customEndYearBoxRef.current.value);
        }

        return customEndYear;
    };

    const receiveGeneralPublicationsPerYear = useCallback((publicationData: { [key: string]: { [key: string]: string } }) => {
        if (currentPage !== 1) return;

        const labels: string[] = [];
        const scienceDatasetData: number[] = [];
        const patentDatasetData: number[] = [];
        const today = new Date();

        const customStartYear: number | undefined = getCustomStartYear();
        const customEndYear: number | undefined = getCustomEndYear();

        if (timeRange === PartnerTimeRangeEnum.LastYear) {
            today.setMonth(today.getMonth() - 13);
            for (let i = 12; i > 0; i--) {
                today.setMonth(today.getMonth() + 1);
                labels.push(`${today.getFullYear()}-${today.getMonth() + 1}`);
            }
        } else if (timeRange === PartnerTimeRangeEnum.Custom) {
            if (customEndYear) {
                if (customStartYear) {
                    const yearDifference = customEndYear - customStartYear;
                    const iStart = yearDifference > 10 ? customEndYear - 9 : customEndYear - yearDifference;
                    for (let i = iStart; i <= customEndYear; i++) {
                        labels.push(i.toString());
                    }
                } else {
                    for (let i = 0; i < 10; i++) {
                        labels.push((customEndYear - i).toString());
                    }
                }
            } else if (customStartYear) {
                for (let i = 0; i < 10; i++) {
                    labels.push((customStartYear + i).toString());
                }
            }
        } else {
            let minusYears;
            switch (timeRange) {
                case PartnerTimeRangeEnum.Last5Years:
                    minusYears = 5;
                    break;
                case PartnerTimeRangeEnum.All:
                case PartnerTimeRangeEnum.Last10Years:
                default:
                    minusYears = 10;
                    break;
            }
            for (let i = 2022 - minusYears; i < 2022; i++) {
                labels.push(i.toString());
            }
        }


        if ("patents" in publicationData) {
            const patentData = publicationData["patents"];
            for (let i = 0; i < labels.length; i++) {
                const currentLabel = labels[i];
                if (currentLabel in patentData) {
                    const patentYearValue = patentData[currentLabel];
                    if (patentYearValue) patentDatasetData.push(parseInt(patentYearValue));
                } else {
                    patentDatasetData.push(0);
                }
            }
        }

        if ("science" in publicationData) {
            const scienceData = publicationData["science"];
            for (let i = 0; i < labels.length; i++) {
                const currentLabel = labels[i];
                if (currentLabel in scienceData) {
                    const patentYearValue = scienceData[currentLabel];
                    if (patentYearValue) scienceDatasetData.push(parseInt(patentYearValue));
                } else {
                    scienceDatasetData.push(0);
                }
            }
        }

        setPublicationsOverTimeData({
            labels: labels,
            datasets: [
                {
                    label: "Science",
                    data: scienceDatasetData,
                    backgroundColor: "rgb(54, 162, 235)"
                },
                {
                    label: "Patents",
                    data: patentDatasetData,
                    backgroundColor: "rgb(75, 192, 192)"
                }
            ]
        });
    }, [timeRange, currentPage]);

    const subscribeToResultFunctions = useCallback(async () => {
        await webSocketController.addHandler("ReceivePartnerCount", receivePartnerCount);
        await webSocketController.addHandler("ReceiveGroupPublicationsPerYear", receiveGeneralPublicationsPerYear);
        await webSocketController.addHandler("ReceiveAllPartnerResults", receivePartnerResults);
        await webSocketController.addHandler("ReceiveSearchID", receiveSearchId);
        await webSocketController.addHandler("ReceiveEndProgress", receiveEndProgress);
        await webSocketController.addHandler("ReceiveSnackbar", receiveSnackbar);
    }, [receiveSearchId, receivePartnerCount, receivePartnerResults, receiveGeneralPublicationsPerYear, webSocketController]);

    const unsubscribeToResultFunctions = useCallback(() => {
        webSocketController.removeHandler("ReceivePartnerCount", receivePartnerCount);
        webSocketController.removeHandler("ReceiveGroupPublicationsPerYear", receiveGeneralPublicationsPerYear);
        webSocketController.removeHandler("ReceiveAllPartnerResults", receivePartnerResults);
        webSocketController.removeHandler("ReceiveSearchID", receiveSearchId);
        webSocketController.removeHandler("ReceiveEndProgress", receiveEndProgress);
        webSocketController.removeHandler("ReceiveSnackbar", receiveSnackbar);
    }, [receiveSearchId, receivePartnerCount, receivePartnerResults, receiveGeneralPublicationsPerYear, webSocketController]);

    useEffect(() => {
        subscribeToResultFunctions();

        return () => unsubscribeToResultFunctions();
    }, [subscribeToResultFunctions, unsubscribeToResultFunctions]);

    async function searchClicked(paginateToPage: number) {
        if (!savedQuery || !selectedOriginalTechnology || !caseState.selectedCaseId) return;

        const filterVariables: TFindOrganizationsDTO = {
            caseId: caseState.selectedCaseId,
            technologyName: selectedOriginalTechnology.name,
            timeRange: timeRange,
            doFindCompanies: doFindCompanies,
            doFindResearchInstitutes: doFindResearchInstitutes,
            doFindUniversities: doFindUniversities,
            technologyId: selectedOriginalTechnology.id !== TechnologyConstants.GENERAL_TECHNOLOGY_ID ? selectedOriginalTechnology.id : undefined,
            excludedPartners: paginateToPage === 1 ? [] : alreadyPaginatedPartners,
            savedQueryId: savedQuery.guid
        };

        await webSocketController.invokeFunction("StartFindOrganizations", filterVariables);
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}OrganizationSearch`, {
            caseId: caseState.selectedCaseId,
            technologyName: selectedOriginalTechnology.name,
            doFindCompanies: doFindCompanies,
            doFindResearchInstitutes: doFindResearchInstitutes,
            doFindUniversities: doFindUniversities,
            page: paginateToPage
        });

        setIsSearching(true);
        setIsQueryCollapsed(true);
        if (paginateToPage === 1) {
            setPartners([]);
            setAlreadyPaginatedPartners([]);
        }
    }

    const onCancelSearch = () => {
        if(!searchId) return;

        webSocketController.invokeFunction("CancelSearch", searchId);
        setIsSearching(false);
    };

    const mostActiveOrganizationOptions = createdStackBarChartOptions("Most active organizations.");
    const publicationsOverTimeOptions = createdStackBarChartOptions("Amount of publications over time.");

    const getQueryContainerClassName = (): string => {
        const classNameList = [styles.selectedQueryContainer];
        classNameList.push(styles.scrollbarCustomThin);
        if (isQueryCollapsed) classNameList.push(styles.collapsed);
        return classNameList.join(" ");
    };

    const getUserFlowQueryContainerClassName = (): string => {
        const classNameList = [styles.userFlowQueryContainer];
        if (!isQueryCollapsed) classNameList.push(styles.uncollapsed);
        return classNameList.join(" ");
    };

    const onHighlight = (documentId: string, documentType: DocumentTypeEnum) => {
        // safety-checks
        if (!caseState.selectedCaseId) { return; }
        const queryId = savedQuery ? savedQuery.guid : null;
        
        if(documentType === DocumentTypeEnum.ScienceArticle) {            
            webSocketController.invokeFunction("OviHighlightArticle", caseState.selectedCaseId, documentId, 1, null, queryId);
            PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}HighlightScienceArticle`, {
                caseId: caseState.selectedCaseId, 
                documentId: documentId,
                queryId: queryId
            });
        } else if(documentType === DocumentTypeEnum.Patent) {
            // This regex checks if the document id is only numbers
            const isMagPatent = RegexConstants.IS_DIGITS_ONLY.test(documentId.trim());
            webSocketController.invokeFunction("OviHighlightArticle", caseState.selectedCaseId, documentId, 
                isMagPatent ? 1 : 2, null, queryId);
            PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}HighlightPatent`, {
                caseId: caseState.selectedCaseId, 
                documentId: documentId,
                queryId: queryId
            });
        }
    };

    const receiveEndProgress = () => {
        setIsSearching(false);
        setSearchId(undefined);
    };

    const receiveSnackbar = (toastType: string, message: string) => {
        if(toastType.toLowerCase() === "error") {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, message);
            PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}ReceivedErrorSnackbar`, {
                error: message
            });
        }
    };
  
    const addToCaseQueryCount = (count: number) => {
        setCaseQueryCount(caseQueryCount + count);
        caseDispatch({ type: CaseActionTypeEnum.SetCaseQueryCount, newQueryCount: caseQueryCount + count } as ICaseSetQueryCount);
    };

    const addToTechnologyQueryCount = (technologyId: number, count: number) => {
        const newList = [];

        for (const technology of technologies) {
            if(technology.id === technologyId) {
                if(technology.queryCount !== undefined) technology.queryCount += count;
            }

            newList.push(technology);
        }

        setTechnologies(newList);
    };

    useEffect(() => {
        if (!selectedOriginalTechnology) return;
        
        caseDispatch({ type: CaseActionTypeEnum.SetCaseSelectedTechnology, 
            selectedOriginalTechnologyId: selectedOriginalTechnology.id, 
            selectedTechnologyId: undefined,
            selectedEntityName: "",
            selectedEntityNumbering: ""} as ICaseSelectedTechnology);
        
    }, [caseDispatch, selectedOriginalTechnology]);
    
    const renderSearchButtonComponent = () => {
        if (isSavedQueriesLoading || isConceptListLoading) {
            return <div></div>;
            // return <SearchButtonLoader />;
        }
        if (savedQuery && !isConceptListLoading && !isSavedQueriesLoading)  {
            return (
                <FindestButton title="Search" onClick={() => { setPartnerCount(undefined); searchClicked(1); }} />
            );
        }
        return null;
    };

    return (
        <>
            <AsynchronousWrapper didError={didConceptListError} errorMessage={conceptListErrorMessage} isLoading={false} LoaderComponent={null}>
                <div className={getUserFlowQueryContainerClassName()}>
                    <div className={styles.querySelectionContainer}>
                        {caseState.selectedCaseId ?
                            <SavedQueries
                                technologies={technologies}
                                selectedTechnology={selectedOriginalTechnology}
                                handleSelectedTechnologyChange={setSelectedOriginalTechnology}
                                caseQueryCount={caseState.selectedCase?.queryCount ? caseState.selectedCase?.queryCount : 0}
                                isLoading={isConceptListLoading}
                            />
                            :
                            null
                        }
                        {renderSearchButtonComponent()}
                    </div>
                    { caseState.selectedCaseId && selectedOriginalTechnology ?
                        <QueryTabs
                            caseId={caseState.selectedCaseId}
                            selectedTechnologyId={selectedOriginalTechnology.id} 
                            addToCaseQueryCount={addToCaseQueryCount}
                            addToTechnologyQueryCount={addToTechnologyQueryCount}
                            isLoading={isConceptListLoading}
                        />
                        : null
                    }
                    {savedQuery ?
                        <div className={getQueryContainerClassName()}>
                            <SavedQueryFields isSpecificationUnitsEnabled={false}/>
                            <div className={styles.selectedQuerySearchFieldsContainer}>
                                <FindOrganizationsQueryFields
                                    customStartYearBoxRef={customStartYearBoxRef}
                                    customEndYearBoxRef={customEndYearBoxRef}
                                    doFindCompanies={doFindCompanies}
                                    handleDoFindestCompaniesChange={setDoFindestCompanies}
                                    doFindResearchInstitutes={doFindResearchInstitutes}
                                    handleDoFindResearchInstitutesChange={setDoFindResearchInstitutes}
                                    doFindUniversities={doFindUniversities}
                                    handleDoFindestUniversitiesChange={setDoFindestUniversities}
                                    timeRange={timeRange}
                                    handleTimeRangeChange={setTimeRange}
                                />
                            </div>
                        </div>
                        :
                        <></>
                    }
                    {/*(isConceptListLoading || isSavedQueriesLoading) && <ViewQueryLoader />*/}
                    {savedQuery && !isConceptListLoading && !isSavedQueriesLoading ?
                        <div className={styles.toggleVisibleQuery}>
                            {isQueryCollapsed ?
                                <div onClick={() => { setIsQueryCollapsed(false); }}>View/Edit Query<FontAwesomeIcon icon={faChevronDown} /></div>
                                :
                                <div onClick={() => { setIsQueryCollapsed(true); }}>Hide Query<FontAwesomeIcon icon={faChevronUp} /></div>
                            }
                        </div>
                        :
                        <></>
                    }
                </div>
            </AsynchronousWrapper>
            <div className={styles.userFlowContentContainer}>
                <div className={styles.headerContainer}>
                    {partnerCount ?
                        <div className={styles.numberOfResults}>{`Found ${partnerCount} organizations working on the subject.`}</div>
                        : <></>}
                    {partnerCount && <Pagination currentPagePosition={currentPage} maximumPagePosition={amountOfPages} goToNextPage={paginateToNextPage} goToPreviousPage={paginateToPreviousPage} />}
                </div>

                {isSearching ? <div className={styles.loadingIndicatorContainer}><RotatingCircleLoadingIndicator onCancel={onCancelSearch} /></div> : null}
                
                <div className={[styles.contentContainer, styles.scrollbarCustomThin].join(" ")}>
                    {mostActiveOrganizationData ?
                        <div>
                            <BarChart data={mostActiveOrganizationData} options={mostActiveOrganizationOptions} />
                        </div>
                        : <></>}
                    {publicationsOverTimeData ?
                        <div>
                            <BarChart data={publicationsOverTimeData} options={publicationsOverTimeOptions} />
                        </div>
                        : <></>}
                    <div>
                        {partners.map((partner) => {
                            partner.isBookmarked = BookmarkLogicSingleton.isResultBookmarked(partner.coworkerBookmarkOn);
                            return (
                                <PartnerResult partner={partner} key={partner.name} onHighlight={onHighlight} />
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
};
